import { AudioLines, Play } from "lucide-react";
import type { BlogPostSummary } from "@/lib/blog";
import { isScheduledPost } from "@/lib/blog-visibility";
import type { Messages } from "@/lib/i18n";
import { formatPostDate } from "@/utils/date";
import inextLogo from "@/assets/inext-logo.svg";
import { BLOG_TAG_LABELS } from "./tag-labels";

export function DraftBadge() {
  return (
    <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">
      Draft
    </span>
  );
}

export function ScheduledBadge() {
  return (
    <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-sky-800">
      Scheduled
    </span>
  );
}

// Small overlay badges flagging that a post embeds audio and/or video, shown in
// the top-right of a card's thumbnail. Renders nothing when the post has no
// media so callers can drop it in unconditionally.
export function MediaBadges({
  post,
  messages,
}: {
  post: Pick<BlogPostSummary, "hasAudio" | "hasVideo">;
  messages: Messages;
}) {
  if (!post.hasAudio && !post.hasVideo) return null;
  return (
    <div className="absolute right-3 top-3 flex items-center gap-1.5">
      {post.hasVideo && (
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white ring-1 ring-white/15 backdrop-blur-sm"
          title={messages.blogMediaVideo}
          aria-label={messages.blogMediaVideo}
        >
          <Play className="h-3.5 w-3.5 fill-current" />
        </span>
      )}
      {post.hasAudio && (
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white ring-1 ring-white/15 backdrop-blur-sm"
          title={messages.blogMediaAudio}
          aria-label={messages.blogMediaAudio}
        >
          <AudioLines className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
}

interface BlogPostCardProps {
  post: BlogPostSummary;
  href: string;
  messages: Messages;
  animationDelay?: number;
}

export function BlogPostCard({ post, href, messages, animationDelay }: BlogPostCardProps) {
  return (
    <a
      href={href}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-elevated)] transition-all duration-500 hover:-translate-y-1 hover:border-primary/30 hover:shadow-glow animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
      style={animationDelay !== undefined ? { animationDelay: `${animationDelay}ms` } : undefined}
    >
      <div className="relative aspect-video w-full overflow-hidden bg-muted">
        {post.heroImage ? (
          <img
            src={post.heroImage}
            alt={post.title}
            loading="lazy"
            className="h-full w-full object-cover object-left"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-muted/50">
            <img src={inextLogo} alt="inext" className="h-8 w-auto opacity-20" />
          </div>
        )}
        <MediaBadges post={post} messages={messages} />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="flex min-w-0 items-center gap-1.5 text-xs font-medium">
          {!post.published && <DraftBadge />}
          {post.published && isScheduledPost(post) && <ScheduledBadge />}
          <span className="shrink-0 whitespace-nowrap text-muted-foreground">
            {formatPostDate(post.publishedAt, post.locale)}
          </span>
          {post.tags.length > 0 && (
            <>
              <span className="shrink-0 text-muted-foreground/50">•</span>
              <span className="min-w-0 truncate text-primary">
                {(messages[BLOG_TAG_LABELS[post.tags[0]] as keyof typeof messages] as string) ||
                  post.tags[0]}
              </span>
            </>
          )}
        </p>
        <h3 className="mt-4 line-clamp-2 text-xl font-semibold">{post.title}</h3>
        <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{post.excerpt}</p>
      </div>
    </a>
  );
}
